/**
 * The public candidate report, reached by share token.
 *
 * Serves both /report/:token and /public-report/:token, and renders ReportV2.
 * /detailed-report and /admin/session/:id render ReportV2 too (22 Oct 2026).
 */

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import ReportV2 from "@/components/report/ReportV2";
import { Card, CardContent } from "@/components/ui/card";
import { AlertTriangle, Loader2 } from "lucide-react";

const PublicReport = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [session, setSession] = useState<any>(null);
  const [report, setReport] = useState<any>(null);
  const [questions, setQuestions] = useState<any[]>([]);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [proctoringEvents, setProctoringEvents] = useState<any[]>([]);
  const [candidateAnswers, setCandidateAnswers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchReport = async () => {
      if (!token) {
        setError("Invalid report link");
        setLoading(false);
        return;
      }

      try {
        // One RPC (V1): anon cannot read the tables. It resolves the share token
        // (or an old link's session token), counts the view, and returns the
        // report without email, phone, CV or payment fields.
        const { data, error: rpcError } = await (supabase.rpc as any)("public_report", { p_token: token });

        if (rpcError || !data?.report || !data?.session) {
          setError("Report not found or sharing is disabled");
          setLoading(false);
          return;
        }

        const fetchedSession = data.session;
        setReport(data.report);
        setSession(fetchedSession);
        setProctoringEvents(data.proctoring_events || []);

        const answersData = fetchedSession.answers || {};
        const answersObject: Record<string, any> = typeof answersData === 'object' && !Array.isArray(answersData)
          ? answersData as Record<string, any>
          : {};
        setAnswers(answersObject);

        const mappedQuestions = (data.questions || []).map((q: any) => ({
          ...q,
          type: q.question_type,
          question: q.question || q.question_text
        }));
        if (fetchedSession.randomized_questions) {
          setQuestions(fetchedSession.randomized_questions
            .map((id: string) => mappedQuestions.find((q: any) => q.id === id))
            .filter(Boolean));
        } else {
          setQuestions(mappedQuestions);
        }

        // Server-side answers, for the code a candidate actually submitted and
        // the executor's own record of running it. The copy on the session is
        // browser-written and can be missing or stale, so the report never
        // renders from it.
        setCandidateAnswers(data.candidate_answers || []);

      } catch (err) {
        console.error("Error fetching report:", err);
        setError("Failed to load report");
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-16 h-16 mx-auto mb-4">
            <Loader2 className="w-16 h-16 text-purple-600 animate-spin" />
          </div>
          <p className="text-gray-600 text-lg">Loading assessment report...</p>
        </div>
      </div>
    );
  }

  if (error || !report || !session) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Report Not Available</h2>
            <p className="text-gray-600">{error || "This report link is invalid or has expired."}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Everything shown below is derived from test_reports.evaluation_details by
  // ReportV2. The session's own `answers` blob is deliberately not passed in.
  return (
    <ReportV2
      session={session}
      report={report}
      questions={questions}
      candidateAnswers={candidateAnswers}
      proctoringEvents={proctoringEvents}
      isPublic={true}
    />
  );
};

export default PublicReport;
